import isEqual from 'lodash/isEqual'
import { useCallback, useMemo } from 'react'
import createPersistedState from 'use-persisted-state'
import { DIVIDER } from '../utils'
import { useMemoCompare } from './useMemoCompare'
import { useSessionId } from './useSessionId'

/**
 * @description Set and read a value from localStorage
 * @param {string} key - localStorage key
 * @param {any} [initialValue] - initial value
 * @param {boolean} [session] - if true, session id will be added to the key
 * @returns {[string, function]} [value, setValue]
 **/
export const useLocalStorage = (_key, initialValue, session = false) => {
	const sessionId = useSessionId()
	const key = _key + (session && sessionId ? `${DIVIDER}${sessionId}` : '')
	const useLocalStorageKey = createPersistedState(key)
	const [storageValue, setStorageValue] = useLocalStorageKey({})
	const { value: value = initialValue, expireTime } = storageValue || {}

	const memoValue = useMemo(
		() => (expireTime && new Date().getTime() > new Date(expireTime).getTime() ? initialValue : value),
		[value, initialValue, expireTime]
	)

	const parsedValue = useMemoCompare(memoValue, (prev, next) => {
		if (expireTime && new Date().getTime() > new Date(expireTime).getTime()) {
			return false
		}
		return isEqual(prev, next)
	})

	const handleSetValue = useCallback(
		(value, expireTime) => {
			if (expireTime && !(expireTime instanceof Date)) {
				throw new Error('expireTime must be a Date')
			}
			if (typeof value === 'function') {
				value = value(parsedValue)
			}

			setStorageValue({ value, expireTime })
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[parsedValue]
	)

	return [parsedValue, handleSetValue]
}
